import { useEffect, useRef } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import type { DummyWorkOrder } from '../dummy'

interface Stat {
  label: string
  value: number
  total: number
  totalLabel: string
}

export function StatRibbon({ workOrders, hiddenScheduledCount = 0 }: {
  workOrders: DummyWorkOrder[]
  /** Real (non-preview) scheduled rows excluded from `workOrders` by the
   *  page's default fetch — see WorkOrdersPage.tsx's scheduledCountQ.
   *  Folded into the denominator the same "X · +Y" way KanbanView's
   *  Scheduled column shows real vs. preview counts, so this ribbon's
   *  total and the Overview card's total agree instead of one silently
   *  excluding not-yet-active items the other counts. */
  hiddenScheduledCount?: number
}) {
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
  const todayEnd   = todayStart.getTime() + 24 * 60 * 60 * 1000

  // Exclude synthetic "upcoming preview" rows (see KanbanView's Scheduled
  // column) — they're computed future occurrences of a recurring
  // template, not real persisted work orders/tasks, and were inflating
  // every stat here (most visibly the "/ total" denominator) by however
  // many preview instances happened to be in the current look-ahead
  // window.
  const real = workOrders.filter((w) => !w._raw.is_preview)

  const total      = real.length
  const trueTotal  = total + hiddenScheduledCount
  const totalLabel = hiddenScheduledCount > 0 ? `${total} · +${hiddenScheduledCount} scheduled` : `${total}`
  const open       = real.filter((w) => w.status === 'open').length
  const inProgress = real.filter((w) => w.status === 'in_progress').length
  // Compare against the start of today, not the exact current moment —
  // due_date has no time component, so `< now` counted anything due
  // "today" as overdue for the rest of the day (a midnight due_date is
  // always < a same-day timestamp). Matches the Overview's Overdue
  // count, which already compares date-to-date server-side.
  const overdue    = real.filter((w) => w.dueDate.getTime() < todayStart.getTime() && w.status !== 'closed' && w.status !== 'resolved').length
  const dueToday   = real.filter(
    (w) => w.dueDate.getTime() >= todayStart.getTime() && w.dueDate.getTime() < todayEnd && w.status !== 'closed' && w.status !== 'resolved',
  ).length

  const stats: Stat[] = [
    { label: 'Open',        value: open,       total: trueTotal, totalLabel },
    { label: 'In progress', value: inProgress, total: trueTotal, totalLabel },
    { label: 'Due today',   value: dueToday,   total: trueTotal, totalLabel },
    { label: 'Overdue',     value: overdue,    total: trueTotal, totalLabel },
  ]

  return (
    <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
      {stats.map((s, i) => (
        <StatCard key={s.label} stat={s} index={i} />
      ))}
    </Stack>
  )
}

function StatCard({ stat, index }: { stat: Stat; index: number }) {
  const pct = stat.total > 0 ? (stat.value / stat.total) * 100 : 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
      style={{ flex: 1, minWidth: 0 }}
    >
      <Box
        sx={{
          px: 2, py: 1.35,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'border-color 160ms ease',
          '&:hover': {
            borderColor: (t) =>
              t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 10.5, fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {stat.label}
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 1 }}>
          <AnimatedNumber
            value={stat.value}
            sx={{
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              color: 'text.primary',
            }}
          />
          <Typography
            sx={{
              fontSize: 12, fontWeight: 500,
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            / {stat.totalLabel}
          </Typography>
        </Stack>

        {/* Subtle neutral progress bar */}
        <Box
          sx={{
            position: 'relative',
            height: 3,
            borderRadius: 99,
            bgcolor: (t) =>
              t.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ delay: index * 0.05 + 0.1, duration: 0.9, ease: [0.32, 0.72, 0, 1] }}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 99,
              background: 'currentColor',
              opacity: 0.4,
            }}
          />
        </Box>
      </Box>
    </motion.div>
  )
}

// ─── Animated integer counter ───────────────────────────────────────────────

function AnimatedNumber({
  value, sx,
}: {
  value: number
  sx?: React.ComponentProps<typeof Typography>['sx']
}) {
  const motionValue = useMotionValue(0)
  const rounded = useTransform(motionValue, (v) => Math.round(v).toString())
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: 0.7,
      ease: [0.32, 0.72, 0, 1],
    })
    return () => controls.stop()
  }, [value, motionValue])

  useEffect(() => {
    const unsub = rounded.on('change', (v) => {
      if (ref.current) ref.current.textContent = v
    })
    return unsub
  }, [rounded])

  return (
    <Typography component="span" ref={ref} sx={sx}>
      0
    </Typography>
  )
}
